import React from 'react';
import { TableHeader } from '@oysterjs/ui/table';
import { type ColumnDef } from '@oysterjs/ui/data-table';
import { Checkbox } from '@oysterjs/ui/checkbox';
import {
  CaraWorkspaceArtifactSummaryFieldsFragment,
  CaraWorkspaceArtifactType
} from '~/api/graphql';
import {
  type DisplayArtifactType,
  getDisplayLabel,
  formatDate,
  compareDates
} from './documents.utils';

export type DocumentSortKey = 'date' | 'name' | 'user' | 'type' | 'clientName';
export type DocumentGroupKey = never;
type ArtifactSummary = CaraWorkspaceArtifactSummaryFieldsFragment;

const uniq = <T,>(arr: T[]) => [...new Set(arr)];

const getArtifactCreatedDate = (artifact: CaraWorkspaceArtifactSummaryFieldsFragment): Date =>
  new Date(artifact.createdAt);

function getAgencyUserName(
  metadata: CaraWorkspaceArtifactSummaryFieldsFragment['metadata']
): string {
  if (metadata && 'agencyUser' in metadata) {
    const agencyUser = (metadata as { agencyUser?: { name?: string } }).agencyUser;
    return agencyUser?.name ?? '';
  }
  return '';
}

export function getDocumentColumns({
  selectedType,
  selectedSet,
  setSelectedArtifacts,
  filtered
}: {
  selectedType: DisplayArtifactType | 'all';
  selectedSet: ReadonlySet<string>;
  setSelectedArtifacts: React.Dispatch<React.SetStateAction<string[]>>;
  filtered: ArtifactSummary[];
}): ColumnDef<ArtifactSummary, DocumentSortKey, DocumentGroupKey>[] {
  const selectCol: ColumnDef<ArtifactSummary, DocumentSortKey, DocumentGroupKey> = {
    id: 'select',
    noHref: true,
    cellClassName: 'w-0 py-1.5 px-3',
    renderHeader: () => (
      <TableHeader className="w-0 py-1.5 px-3 text-xs">
        <Checkbox
          checked={selectedSet.size > 0 && selectedSet.size === filtered.length}
          color="sky"
          onChange={(v) => {
            setSelectedArtifacts((prev) =>
              v ? uniq([...prev, ...filtered.map((artifact) => artifact.id)]) : []
            );
          }}
        />
      </TableHeader>
    ),
    renderCell: (artifact) => (
      <Checkbox
        checked={selectedSet.has(artifact.id)}
        color="sky"
        onChange={(v) => {
          setSelectedArtifacts((prev) =>
            v ? uniq([...prev, artifact.id]) : prev.filter((id) => id !== artifact.id)
          );
        }}
      />
    )
  };

  const nameCol: ColumnDef<ArtifactSummary, DocumentSortKey, DocumentGroupKey> = {
    id: 'name',
    header: 'File name',
    sortKey: 'name',
    renderCell: (artifact) => (
      <span
        className="flex items-center gap-1.5 truncate text-sm font-medium text-neutral-900 dark:text-white"
        title={artifact.title}
      >
        <span className="truncate">{artifact.title}</span>
      </span>
    )
  };

  const typeCol: ColumnDef<ArtifactSummary, DocumentSortKey, DocumentGroupKey> = {
    id: 'type',
    header: 'Type',
    sortKey: 'type',
    renderCell: (artifact) => {
      const label =
        artifact.type === CaraWorkspaceArtifactType.TemplateForm
          ? artifact.title
          : getDisplayLabel(artifact.type);
      return (
        <span
          className="block truncate text-sm text-neutral-600 dark:text-neutral-400"
          title={label}
        >
          {label}
        </span>
      );
    }
  };

  const clientNameCol: ColumnDef<ArtifactSummary, DocumentSortKey, DocumentGroupKey> = {
    id: 'clientName',
    header: 'Client name',
    sortKey: 'clientName',
    renderCell: (artifact) => {
      const clientName = artifact.metadata.clientName || '—';
      const workspaceTitle = artifact.metadata.workspaceTitle || '';
      return (
        <div className="block truncate">
          <span
            className="block truncate text-sm text-neutral-600 dark:text-neutral-400"
            title={clientName}
          >
            {clientName}
          </span>
          {workspaceTitle && (
            <span
              className="block truncate text-xs text-neutral-500 dark:text-neutral-500"
              title={workspaceTitle}
            >
              {workspaceTitle}
            </span>
          )}
        </div>
      );
    }
  };

  const userCol: ColumnDef<ArtifactSummary, DocumentSortKey, DocumentGroupKey> = {
    id: 'user',
    header: 'Generated by',
    sortKey: 'user',
    renderCell: (artifact) => {
      const userName = getAgencyUserName(artifact.metadata) || '—';
      return (
        <span
          className="block truncate text-sm text-neutral-600 dark:text-neutral-400"
          title={userName}
        >
          {userName}
        </span>
      );
    }
  };

  const dateCol: ColumnDef<ArtifactSummary, DocumentSortKey, DocumentGroupKey> = {
    id: 'date',
    header: 'Date Generated',
    sortKey: 'date',
    renderCell: (artifact) => (
      <span className="text-xs text-neutral-600 dark:text-neutral-400">
        {formatDate(getArtifactCreatedDate(artifact))}
      </span>
    )
  };

  if (selectedType === 'all') {
    return [selectCol, nameCol, typeCol, clientNameCol, userCol, dateCol];
  }

  return [selectCol, nameCol, clientNameCol, userCol, dateCol];
}

export function documentSortFn(
  data: CaraWorkspaceArtifactSummaryFieldsFragment[],
  sortKey: DocumentSortKey,
  direction: 'asc' | 'desc'
): CaraWorkspaceArtifactSummaryFieldsFragment[] {
  const sorted = [...data].sort((a, b) => {
    switch (sortKey) {
      case 'name':
        return a.title.localeCompare(b.title);
      case 'type': {
        const aLabel =
          a.type === CaraWorkspaceArtifactType.TemplateForm ? a.title : getDisplayLabel(a.type);
        const bLabel =
          b.type === CaraWorkspaceArtifactType.TemplateForm ? b.title : getDisplayLabel(b.type);
        return aLabel.localeCompare(bLabel);
      }
      case 'user': {
        const aUser = getAgencyUserName(a.metadata);
        const bUser = getAgencyUserName(b.metadata);
        return aUser.localeCompare(bUser);
      }
      case 'clientName': {
        const aClient = a.metadata.clientName || '';
        const bClient = b.metadata.clientName || '';
        return aClient.localeCompare(bClient);
      }
      case 'date':
        return compareDates(getArtifactCreatedDate(a), getArtifactCreatedDate(b));
      default:
        return 0;
    }
  });
  return direction === 'asc' ? sorted.reverse() : sorted;
}
